<template>
  <div class="step">
    <div class="step-header">
      <div class="step-content">
        <div class="form-field">
          <AppButtonFlat
            color="primary"
            variant="outlined"
            prepend-icon="mdi-plus"
            @click="openAddServiceModal"
          >
            Add service
          </AppButtonFlat>
        </div>
        <!-- <p class="desc">
          
        </p> -->
      </div>
    </div>

    <h2 class="title text-success">Select the services you offer. You can customize details later.</h2>
    
    <!-- <div class="form-field">
      <AppSearchField v-model="search" placeholder="Search services..." />

      <div v-if="serverError" class="api-error">{{ serverError }}</div>
    </div> -->

    <div class="service-list">
      <label
        v-for="(service, index) in filteredServices"
        :key="index"
        class="service-item"
      >
        <div class="service-left">
          <div class="service-title">{{ service.service_name }}</div>
          <div class="service-subtitle">{{ service.subtitle }}</div>
        </div>
        <v-checkbox
          class="service-check"
          density="compact"
          hide-details
          :model-value="selectedServices.includes(service.service_name)"
          @update:modelValue="toggle(service.service_name, Boolean($event))"
        />
      </label>
    </div>

    <div class="selected-hint">
      Selected: <strong>{{ selectedServices.length }}</strong>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import AppSearchField from "@/shared/components/ui/AppSearchField.vue";
import { useModalStore } from "@/shared/stores/modal";
import { useAppStore } from "@/shared/stores/app";
import { getActiveContext } from "@/modules/auth/services/auth.storage";
import AddServiceModal from "@/modules/vendor/components/profile-setup/AddServiceModal.vue";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";

const props = defineProps<{
  current_step?: number;
  errors?: Record<string, any> | null;
  profileInfo?: any;
}>();

type ServiceOption = {
  service_name: string;
  subtitle: string;
  price?: number;
};

const modal = useModalStore();
const appStore = useAppStore();

type ServiceTemplate = { service_name?: string; subtitle?: string };
type ServiceGroup = {
  id?: number | string;
  name?: string;
  label?: string;
  routeName?: string;
  route_name?: string;
  service_templates?: ServiceTemplate[];
};

function normalizeText(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function extractServiceGroupId(profileInfo: any): number | null {
  const direct = Number(profileInfo?.service_group_id ?? profileInfo?.serviceGroupId ?? NaN);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const nested = Number(profileInfo?.service_group?.id ?? NaN);
  if (Number.isFinite(nested) && nested > 0) return nested;

  return null;
}

function extractServiceGroupName(profileInfo: any): string {
  const n1 = normalizeText(profileInfo?.service_group?.name);
  if (n1) return n1;
  const n2 = normalizeText(profileInfo?.service_group_name);
  if (n2) return n2;
  const n3 = normalizeText(profileInfo?.service_group?.label);
  if (n3) return n3;
  return "";
}

function normalizeSlug(value: unknown) {
  return normalizeText(value).replace(/_/g, "-").replace(/\s+/g, "-");
}

const activeVendorType = computed(() => {
  const ctx: any = getActiveContext() || {};
  const vendorTypeRaw = ctx?.vendor?.type ?? ctx?.vendor_type ?? "";
  return normalizeSlug(vendorTypeRaw);
});

function matchesVendorType(group: ServiceGroup, vendorType: string) {
  if (!vendorType) return true;
  const routeName = normalizeSlug(group?.routeName ?? group?.route_name ?? "");
  if (routeName && routeName.includes(vendorType)) return true;
  const name = normalizeSlug(group?.name ?? "");
  if (name && name === vendorType) return true;
  const label = normalizeSlug(group?.label ?? "");
  if (label && label === vendorType) return true;
  return false;
}

const selectedServiceGroup = computed<ServiceGroup | null>(() => {
  const groups = (Array.isArray(appStore.serviceGroups) ? appStore.serviceGroups : []) as ServiceGroup[];
  if (!groups.length) return null;

  // Filter groups by active vendor type (auth_active_context.vendor.type) when present.
  const vendorType = activeVendorType.value;
  const scopedGroups = vendorType ? groups.filter((g) => matchesVendorType(g, vendorType)) : groups;
  if (!scopedGroups.length) return null;

  const groupId = extractServiceGroupId(props.profileInfo);
  if (groupId != null) {
    const byId = scopedGroups.find((g) => Number(g?.id ?? NaN) === groupId) || null;
    if (byId) return byId;
  }

  const groupName = extractServiceGroupName(props.profileInfo);
  if (groupName) {
    const byName =
      scopedGroups.find((g) => normalizeText(g?.name) === groupName) ||
      scopedGroups.find((g) => normalizeText(g?.label) === groupName) ||
      null;
    if (byName) return byName;
  }

  const nonEmpty = scopedGroups.filter((g) => Array.isArray(g?.service_templates) && g.service_templates!.length > 0);
  if (nonEmpty.length === 1) return nonEmpty[0] as ServiceGroup;

  return null;
});

function getAllServiceTemplates(groups: ServiceGroup[] = []): ServiceOption[] {
  const seen = new Set<string>();
  const out: ServiceOption[] = [];
  const vendorType = activeVendorType.value;
  groups.forEach((g) => {
    if (vendorType && !matchesVendorType(g, vendorType)) return;
    const templates = Array.isArray(g?.service_templates) ? g.service_templates : [];
    templates.forEach((t) => {
      const service_name = String(t?.service_name ?? "").trim();
      if (!service_name) return;
      const key = normalizeText(service_name);
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push({
        service_name,
        subtitle: String(t?.subtitle ?? "").trim(),
      });
    });
  });
  return out;
}

const baseServiceOptions = computed<ServiceOption[]>(() => {
  const templates = (selectedServiceGroup.value?.service_templates ?? []) as ServiceTemplate[];
  if (templates.length) {
    return templates
      .map((t) => {
        const service_name = String(t?.service_name ?? "").trim();
        if (!service_name) return null;
        return {
          service_name,
          subtitle: String(t?.subtitle ?? "").trim(),
        } as ServiceOption;
      })
      .filter(Boolean) as ServiceOption[];
  }

  // If nothing is selected/created yet, show all available templates across groups.
  const apiList = Array.isArray(props.profileInfo?.service_list) ? props.profileInfo.service_list : [];
  const hasExistingSelections = selectedServices.value.length > 0;
  const hasAnyOptions = serviceOptions.value.length > 0;
  if (hasExistingSelections || apiList.length > 0 || hasAnyOptions) return [];

  const groups = (Array.isArray(appStore.serviceGroups) ? appStore.serviceGroups : []) as ServiceGroup[];
  return getAllServiceTemplates(groups);
});

const serviceOptions = ref<ServiceOption[]>([]);
const selectedServices = ref<string[]>([]);
const search = ref("");
const serverError = ref("");

onMounted(async () => {
  if (appStore.status !== "ready") {
    await appStore.loadAppInit();
  }
});

watch(
  baseServiceOptions,
  (base) => {
    if (!base.length) return;

    // Treat app-init templates as defaults, but don't drop any existing entries (added via API or modal).
    if (!serviceOptions.value.length) {
      serviceOptions.value = base;
      return;
    }

    const existingMap = new Map(serviceOptions.value.map((s) => [s.service_name, s]));
    const merged = [...serviceOptions.value];
    base.forEach((svc) => {
      if (!existingMap.has(svc.service_name)) merged.push(svc);
    });
    serviceOptions.value = merged;
  },
  { immediate: true }
);

const filteredServices = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return serviceOptions.value;
  return serviceOptions.value.filter((s) => {
    return (
      s.service_name.toLowerCase().includes(q) || s.subtitle.toLowerCase().includes(q)
    );
  });
});

watch(
  () => props.profileInfo,
  (info) => {
    const apiList = Array.isArray(info?.service_list) ? info.service_list : [];
    if (!apiList.length) return;

    const normalized: ServiceOption[] = apiList
      .map((s: any) => {
        const name = String(s?.service_name || "").trim();
        if (!name) return null;
        const subtitle = String(s?.subtitle || "").trim();
        const price = Number(s?.price ?? 0);
        return {
          service_name: name,
          subtitle,
          price: Number.isFinite(price) ? price : 0,
        } as ServiceOption;
      })
      .filter(Boolean) as ServiceOption[];

    if (!normalized.length) return;

    // Merge API options into existing options (do not lose local defaults).
    const existingMap = new Map(serviceOptions.value.map((s) => [s.service_name, s]));
    normalized.forEach((svc) => {
      if (!existingMap.has(svc.service_name)) {
        serviceOptions.value = [svc, ...serviceOptions.value];
      }
    });

    // Prefill selected services only when user hasn't selected anything yet.
    if (!selectedServices.value.length) {
      selectedServices.value = normalized.map((s) => s.service_name);
    }
  },
  { immediate: true }
);

// Map props.errors to a friendly serverError message to display under the search field
watch(
  () => props.errors,
  (errs: any) => {
    serverError.value = "";
    if (!errs) return;
    // Prefer errors.services or any key containing 'service'
    if (errs.services) {
      serverError.value = Array.isArray(errs.services)
        ? errs.services.join(" ")
        : String(errs.services);
      return;
    }
    const serviceKey = Object.keys(errs).find((k) => /service/i.test(k));
    if (serviceKey) {
      const v = errs[serviceKey];
      serverError.value = Array.isArray(v) ? v.join(" ") : String(v);
      return;
    }
    // Fallback: join all error messages into one string
    const msgs: string[] = [];
    Object.values(errs).forEach((val: any) => {
      if (Array.isArray(val)) msgs.push(val.join(" "));
      else if (typeof val === "string") msgs.push(val);
      else if (typeof val === "object") msgs.push(JSON.stringify(val));
    });
    serverError.value = msgs.join(" ");
  },
  { immediate: true }
);

function getValues() {
  // Only include services that the user has selected. Map to the shape expected by the server.
  const servicesToSend = selectedServices.value.map((name) => {
    const svc = serviceOptions.value.find((s) => s.service_name === name);
    return {
      service_name: svc?.service_name || name,
      subtitle: svc?.subtitle || "",
      price: (svc as any)?.price || 0,
    };
  });

  return {
    services: servicesToSend,
    search: search.value,
    selectedServiceIds: selectedServices.value,
  };
}

defineExpose({ getValues });

function openAddServiceModal() {
  const existingIds = serviceOptions.value.map((s) => s.service_name);
  modal.open(
    AddServiceModal,
    {
      existingIds,
      onAdd: (service: ServiceOption) => {
        serviceOptions.value = [service, ...serviceOptions.value];
      },
    },
    { fullscreen: true, title: "Add Service", showHeader: false }
  );
}

function toggle(serviceName: string, isSelected: boolean) {
  if (isSelected) {
    selectedServices.value.push(serviceName);
  } else {
    const index = selectedServices.value.indexOf(serviceName);
    if (index > -1) {
      selectedServices.value.splice(index, 1);
    }
  }
}
</script>

<style scoped>
.step {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 8px;
}

.step-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: var(--radius-lg);
  background: rgba(232, 97, 47, 0.1);
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 4px;
  letter-spacing: -0.025em;
}

.desc {
  margin: 0;
  color: var(--color-subtitle-light);
  font-size: 14px;
  line-height: 1.2;
  font-weight: 400;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.label-icon {
  flex-shrink: 0;
}

.service-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 6px;
}

.service-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
}

.service-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
}

.service-title {
  font-weight: 800;
  color: #111827;
}

.service-subtitle {
  color: #6b7280;
  font-size: 13px;
  line-height: 1.3;
}

.service-check {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.selected-hint {
  margin-top: 4px;
  color: #6b7280;
  font-size: 13px;
}

.api-error {
  color: #dc2626; /* red-600 */
  font-size: 13px;
  margin-top: 8px;
}
</style>
